import { getCompoundV3BaseAsset } from '../../public-data/addresses/compoundV3'
import { COMP_ADDRESSES } from '../../public-data/compound-v3/publicCallParse'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { rebuildCometData, usesCometLens } from './lens'
import { createBaseTypeUserState } from '../utils'
import { LenderCrossPoolMeta, UserData } from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMarketUid } from '../../../utils/marketUid'
import { getLenderAssets } from '../../../utils'

/**
 * Compound III (Comet) pricing context for interpreting user balances below.
 *
 * Each market has one borrowable base token and a dedicated base price feed
 * (`baseTokenPriceFeed`). Every listed collateral has its own feed from
 * `getAssetInfo`. Comet reads each feed via `getPrice` and combines amounts with
 * the same scaling pattern (`mulPrice`); it does not normalize answers into a
 * single quote asset inside the contract.
 *
 * Feed quote assets are deployment-specific (e.g. token/USD, token/ETH,
 * token/BTC, peg-style, or constant feeds for the base). There is no protocol
 * guarantee that all feeds for a Comet share one numeraire—only that governance
 * configures them so borrow and collateral sides are economically comparable.
 *
 * Comet does not define a named “health factor”. A HF-like scalar is the ratio
 * of total risk-adjusted collateral value to borrow value when both sides are
 * valued the way Comet does for liquidation: debt from borrow balance and the
 * base feed; per-asset collateral from balances, that asset’s feed, and
 * `liquidateCollateralFactor` (liquidation threshold, above borrow collateral
 * factor).
 */
export const getCompoundV3UserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  // Use same asset ordering as build function
  const baseAsset = getCompoundV3BaseAsset(lender, chainId)?.baseAsset
  const assets = getLenderAssets(chainId, lender).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  )
  const assetsNoBase = assets.filter((a) => a !== baseAsset)
  const walkCalls = assetsNoBase.length + 2 + 1
  const viaLens = usesCometLens(chainId, lender)
  // lens blob + the rewards read, which is on a different contract.
  const expectedNumberOfCalls = viaLens ? 2 : walkCalls
  return [
    (raw: any[]) => {
      if (raw.length !== expectedNumberOfCalls) {
        return undefined
      }

      // One blob in, the walk's own array out — every index below is unchanged.
      let data: any[]
      if (viaLens) {
        const rebuilt = rebuildCometData(raw[0], assetsNoBase.length)
        if (!rebuilt) return undefined
        data = [...rebuilt, raw[1]]
      } else {
        data = raw
      }
      if (data.length !== walkCalls) {
        return undefined
      }

      let totalDebt24h = 0
      let totalDeposits24h = 0

      let result: { [asset: string]: any } = {}
      for (let i = 0; i < assetsNoBase.length; i++) {
        const asset = assetsNoBase[i]
        const key = createMarketUid(chainId, lender, asset)
        const metaEntity = metaMap?.[key]!
        const assetMeta = metaEntity.asset
        const decimals = assetMeta?.decimals ?? 18

        // a failed read is not a zero balance — skip the collateral entirely
        if (isFailedCall(data[i])) continue

        const depositsRaw = data[i][0]?.toString()

        // no pos if no deposits
        if (depositsRaw === '0') continue

        const deposits = parseRawAmount(depositsRaw, decimals)

        // prices
        const price = getDisplayPrice(metaEntity)
        const oPrice = getOraclePrice(metaEntity)
        const priceHist = metaEntity?.price?.priceUsd24h ?? price
        // accrue
        totalDeposits24h += Number(deposits) * priceHist

        result[key] = {
          marketUid: key,
          underlying: asset,
          deposits,
          depositsUSD: Number(deposits) * price,
          debtStableUSD: 0,
          debt: '0',
          debtStable: '0',
          debtUSD: 0,
          depositsUSDOracle: Number(deposits) * oPrice,
          debtStableUSDOracle: 0,
          debtUSDOracle: 0,
          collateralEnabled: true,
        }
      }

      // The base side is two separate calls (balanceOf / borrowBalanceOf). If
      // either failed we cannot state the base position at all — emitting it
      // would report a borrow of 0 for a user who may well be borrowing.
      const baseFailed =
        isFailedCall(data[assetsNoBase.length]) ||
        isFailedCall(data[assetsNoBase.length + 1])
      const depositsRaw = data[assetsNoBase.length]?.toString()
      const debtRaw = data[assetsNoBase.length + 1]?.toString()
      const key = createMarketUid(chainId, lender, baseAsset)
      const metaEntity = metaMap?.[key]!
      const baseMeta = metaEntity.asset
      const baseDecimals = baseMeta.decimals ?? 18
      const deposits = baseFailed
        ? '0'
        : parseRawAmount(depositsRaw, baseDecimals)
      const debt = baseFailed ? '0' : parseRawAmount(debtRaw, baseDecimals)

      const price = getDisplayPrice(metaEntity)
      const oPrice = getOraclePrice(metaEntity)
      const priceHist = metaEntity?.price?.priceUsd24h ?? price

      // accrue base data
      totalDebt24h += Number(debt) * priceHist
      totalDeposits24h += Number(deposits) * priceHist

      // no pos if the read failed, or if there are no deposits and no debt
      const resultBase =
        baseFailed || (depositsRaw === '0' && debtRaw === '0')
          ? {}
          : {
              [key]: {
                marketUid: key,
                underlying: baseAsset,
                deposits,
                debt,
                depositsUSD: Number(deposits) * price,
                debtUSD: Number(debt) * price,
                depositsUSDOracle: Number(deposits) * oPrice,
                debtUSDOracle: Number(debt) * oPrice,
                // populate values for totals
                debtStable: '0',
                debtStableUSD: 0,
                debtStableUSDOracle: 0,
                collateralEnabled: false,
              },
            }

      // the result is an array: [tokenAddress, owedAmount] (tokenAddress should be always COMP)
      // so we take [1] to get the owedAmount
      const earnedBalances = data[walkCalls - 1]

      const compRewards = Number(
        parseRawAmount(earnedBalances?.owed?.toString(), 18),
      )
      const payload = {
        lendingPositions: { ...result, ...resultBase },
        chainId,
        baseAsset,
        rewards: COMP_ADDRESSES[chainId]
          ? [
              {
                asset: COMP_ADDRESSES[chainId],
                totalRewards: compRewards,
                claimableRewards: compRewards,
              },
            ]
          : [],
        account,
      }
      const userData = createBaseTypeUserState(
        payload,
        metaMap!,
        totalDeposits24h,
        totalDebt24h,
      )
      return userData
    },
    expectedNumberOfCalls,
  ]
}
